import { useEffect, useMemo, useState } from 'react';
import { downloadCsv } from '../utils/csv.js';
import { sectorBlankLabel } from './BreakdownGrid.jsx';

// Splits the currently filtered contacts into outreach batches. Paste the
// schedule rows from the planning sheet (or just a list of sizes); each
// contact is dealt to a batch so that no fund/email-domain is dumped into a
// single batch — a firm's contacts spread across as many batches as the
// sizes allow. One-shot: nothing is stored, the CSV (one row per contact
// with a Batch column) is the artifact.
//
// Approved contacts (firm bd_status = approved) get a different email than
// the cold BD sends, so they are set aside BEFORE the deal: batches fill to
// their planned sizes with unapproved contacts only (planned size = BD send
// size), and the approved ones download as their own flat CSV
// (email / fund / name / sector pack) for the separate send.

// Personal-mail domains say nothing about employer — treat each such
// contact as unrelated to the others rather than as one "firm".
const FREEMAIL = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'hotmail.com',
  'outlook.com', 'live.com', 'msn.com', 'aol.com', 'icloud.com', 'me.com',
  'mac.com', 'protonmail.com', 'proton.me', 'comcast.net', 'verizon.net',
]);

// Local BD import agent (scripts-bd: `npm run agent`). It runs on the
// user's machine and types names into BD Advanced's Create List page; the
// panel just polls localhost, which behaves the same whether the dashboard
// is served from localhost or Cloud Run.
const BD_AGENT = 'http://127.0.0.1:4599';
// Includes the report-download states (Sync BigDough's fetch feature) and the
// queue's waiting-save — the agent runs one browser job at a time, so →BD
// stays disabled during those too.
const AGENT_ACTIVE = new Set(['opening', 'waiting-login', 'running', 'waiting-save', 'waiting-tracking', 'scraping', 'downloading']);

// Deterministic RNG so a given (contacts, plan, seed) always yields the
// same split — "Reshuffle" bumps the seed for a fresh deal.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle(arr, rng) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Parse pasted plan rows. Accepts raw TSV rows copied from the sheet
// (Date / Day / Target Time / Audience / # of Contact / Contact List-Batch —
// extra columns like links are ignored), or a bare comma/newline list of
// sizes. Per sheet row: the first integer cell is the batch size, the next
// non-URL cell after it is the batch name, and everything before it
// (date, day, time, audience) is kept as a label.
// BD lists are named with the send date up front: "0927-Consumer UA".
function mmddPrefix(dateCell) {
  let m, d;
  if (dateCell) [m, d] = dateCell.split('/');
  else { const t = new Date(); m = String(t.getMonth() + 1); d = String(t.getDate()); }
  return `${m.padStart(2, '0')}${d.padStart(2, '0')}`;
}

export function parsePlan(text) {
  const batches = [];
  for (const rawLine of String(text ?? '').split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const cells = (line.includes('\t') ? line.split('\t') : line.split(',')).map((s) => s.trim());
    if (/^total$/i.test(cells[0] ?? '')) continue;
    const isInt = (c) => /^\d[\d,]*$/.test(c) && Number.parseInt(c.replace(/,/g, ''), 10) > 0;
    // A line that is nothing but numbers is a bare size list: "500, 300, 200".
    const nonEmpty = cells.filter(Boolean);
    if (nonEmpty.length > 0 && nonEmpty.every(isInt) && !line.includes('\t')) {
      for (const c of nonEmpty) {
        const name = `Batch ${batches.length + 1}`;
        batches.push({ name, size: Number.parseInt(c.replace(/,/g, ''), 10), meta: '', saveName: `${mmddPrefix()}-${name}` });
      }
      continue;
    }
    const idx = cells.findIndex(isInt);
    if (idx === -1) continue; // header row or junk
    const size = Number.parseInt(cells[idx].replace(/,/g, ''), 10);
    const name = cells.slice(idx + 1).find((c) => c && !/^https?:/i.test(c)) || `Batch ${batches.length + 1}`;
    // The cell right before the count is the Audience column when present —
    // dates ("9/27/2026") and times ("3pm ET") all start with a digit,
    // audience names don't.
    const before = idx > 0 ? cells[idx - 1] : '';
    const audience = before && !/^\d/.test(before) && !/^https?:/i.test(before) ? before : '';
    const meta = cells.slice(0, audience ? idx - 1 : idx).filter(Boolean).join(' · ');
    const dateCell = cells.slice(0, idx).find((c) => /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(c));
    batches.push({ name, size, meta, audience, saveName: `${mmddPrefix(dateCell)}-${name}` });
  }
  return batches;
}

// Deal contacts to batches, stratified by email domain. Groups (largest
// firm first, shuffled within) deal one contact at a time to the batch with
// the highest remaining-capacity RATIO, so unequal batch sizes fill
// proportionally and a firm's contacts land in as many different batches as
// possible. Ratios may go negative, which spreads any overflow (more
// contacts than planned capacity) proportionally too — nobody is dropped.
export function assignBatches(rows, batches, seed) {
  const rng = mulberry32(seed);
  const groups = new Map(); // domain (or per-contact solo key) → rows
  rows.forEach((r, i) => {
    const email = String(r.email ?? '').trim().toLowerCase();
    const at = email.lastIndexOf('@');
    let key = at > 0 ? email.slice(at + 1) : `(no email) #${i}`;
    if (FREEMAIL.has(key)) key = `${key} #${i}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });

  const remaining = batches.map((b) => b.size);
  const assignment = new Map(); // row → batch index
  const ordered = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
  for (const [, members] of ordered) {
    shuffle(members, rng);
    for (const row of members) {
      let ties = [];
      let best = -Infinity;
      for (let b = 0; b < batches.length; b++) {
        const score = remaining[b] / batches[b].size;
        if (score > best + 1e-9) { best = score; ties = [b]; }
        else if (score > best - 1e-9) ties.push(b);
      }
      const pick = ties[Math.floor(rng() * ties.length)];
      remaining[pick] -= 1;
      assignment.set(row, pick);
    }
  }
  return assignment;
}

// "Industrial", "Industrials" and "industrials " all name the same pack.
const normAudience = (s) => String(s ?? '').toLowerCase().replace(/[^a-z]/g, '').replace(/s$/, '');

// Audience spellings that mean "whoever matched none of the other audiences"
// (normalized through normAudience, so "All others" → "allother" etc.).
const CATCH_ALL_KEYS = new Set(['everyoneelse', 'everybodyelse', 'everyone', 'allother', 'other', 'catchall', 'rest']);

// Which pasted audiences does a contact belong to? "Generalist" is
// coverage-based (mirroring the breakdown grid's blank-sector rule);
// every other audience matches the contact's sector_packs tags.
function matchAudiences(row, audienceKeys) {
  const isGeneralist = String(row.sector_coverage ?? '').toLowerCase().includes('generalist');
  const packs = new Set(String(row.sector_packs ?? '').split(',').map(normAudience).filter(Boolean));
  return audienceKeys.filter((k) => (k === 'generalist' ? isGeneralist : packs.has(k)));
}

// Deal each contact to exactly ONE audience — a contact tagged with two
// scheduled packs must not get two emails. Single-match contacts go straight
// to their audience; multi-pack contacts go to whichever matching audience
// is least full relative to its planned capacity. A catch-all audience
// ("Everyone else") never matches by pack — it collects the no-match rest.
export function assignAudiences(rows, capacities, rng, catchAll = null) {
  const keys = [...capacities.keys()].filter((k) => k !== catchAll);
  const counts = new Map([...capacities.keys()].map((k) => [k, 0]));
  const result = new Map(); // row → audience key, or null for no match
  const multi = [];
  for (const row of rows) {
    const m = matchAudiences(row, keys);
    if (m.length === 0) {
      if (catchAll != null) { result.set(row, catchAll); counts.set(catchAll, counts.get(catchAll) + 1); }
      else result.set(row, null);
    }
    else if (m.length === 1) { result.set(row, m[0]); counts.set(m[0], counts.get(m[0]) + 1); }
    else multi.push([row, m]);
  }
  shuffle(multi, rng);
  for (const [row, m] of multi) {
    let pick = m[0];
    let best = Infinity;
    for (const k of m) {
      const ratio = counts.get(k) / Math.max(1, capacities.get(k));
      if (ratio < best - 1e-9 || (ratio < best + 1e-9 && rng() < 0.5)) { best = ratio; pick = k; }
    }
    counts.set(pick, counts.get(pick) + 1);
    result.set(row, pick);
  }
  return result;
}

// When the pool is bigger than the plan, pick a random subset that fills
// the batches exactly — the rest stay unassigned this week, and the page's
// "not touched recently" filter rotates them in next week.
function samplePool(rows, capacity, rng) {
  if (rows.length <= capacity) return { picked: rows, rest: [] };
  const shuffled = shuffle([...rows], rng);
  return { picked: shuffled.slice(0, capacity), rest: shuffled.slice(capacity) };
}

// Full split: without audiences, one deal over all batches; with audiences
// (paste included the sheet's Audience column), contacts partition by
// audience first, then each audience deals independently into its own
// batches. An audience named "Everyone else" (or similar) is a catch-all:
// contacts matching no other audience land there instead of being dropped.
// Returns row → global batch index (-1 = matched no audience and no
// catch-all pasted, -2 = in the pool but not selected this week), plus
// per-audience matched-vs-capacity stats for the preview.
export function computeSplit(rows, batches, seed) {
  const sampleRng = mulberry32((seed ^ 0x51abc0de) >>> 0);
  if (!batches.some((b) => b.audience)) {
    const capacity = batches.reduce((a, b) => a + b.size, 0);
    const { picked, rest } = samplePool(rows, capacity, sampleRng);
    const assignment = assignBatches(picked, batches, seed);
    for (const r of rest) assignment.set(r, -2);
    return { assignment, audienceStats: null };
  }
  const groups = new Map(); // norm key → { label, indices: global batch idxs }
  batches.forEach((b, i) => {
    const key = normAudience(b.audience) || '(none)';
    if (!groups.has(key)) groups.set(key, { label: b.audience || '(none)', indices: [] });
    groups.get(key).indices.push(i);
  });
  const capacities = new Map([...groups.entries()]
    .map(([k, g]) => [k, g.indices.reduce((a, i) => a + batches[i].size, 0)]));
  const catchAll = [...groups.keys()].find((k) => CATCH_ALL_KEYS.has(k)) ?? null;
  const audAssign = assignAudiences(rows, capacities, mulberry32((seed ^ 0x9e3779b9) >>> 0), catchAll);
  const rowsByAud = new Map([...groups.keys()].map((k) => [k, []]));
  const assignment = new Map();
  let unmatched = 0;
  for (const row of rows) {
    const k = audAssign.get(row);
    if (k == null) { assignment.set(row, -1); unmatched += 1; }
    else rowsByAud.get(k).push(row);
  }
  const audienceStats = new Map();
  let audSeed = seed;
  for (const [k, g] of groups) {
    const members = rowsByAud.get(k);
    const { picked, rest } = samplePool(members, capacities.get(k), sampleRng);
    audienceStats.set(k, { label: g.label, capacity: capacities.get(k), matched: members.length, selected: picked.length });
    const local = assignBatches(picked, g.indices.map((i) => batches[i]), audSeed++);
    for (const [row, li] of local) assignment.set(row, g.indices[li]);
    for (const r of rest) assignment.set(r, -2);
  }
  if (unmatched > 0) audienceStats.set('__unmatched', { label: 'no matching audience', capacity: 0, matched: unmatched });
  return { assignment, audienceStats };
}

function domainOf(row) {
  const email = String(row.email ?? '').trim().toLowerCase();
  const at = email.lastIndexOf('@');
  return at > 0 ? email.slice(at + 1) : '(no email)';
}

// Same definition as the Breakdown grid's "Approved (status)" dimension.
const isApproved = (row) => String(row.bd_status ?? '').toLowerCase() === 'approved';

export default function BatchSplitPanel({ onClose, fetchRows, filterSummary = [] }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [planText, setPlanText] = useState('');
  const [seed, setSeed] = useState(1);
  // null = agent not reachable; otherwise the agent's current job summary
  // ({ state: 'idle' } when it has none).
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${BD_AGENT}/status`);
        const d = await r.json();
        if (!cancelled) setAgent(d.job ?? { state: 'idle' });
      } catch {
        if (!cancelled) setAgent(null);
      }
    };
    poll();
    const t = setInterval(poll, 1500);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchRows()
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
    // fetchRows captures the page's current filters; fetch once per open.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const batches = useMemo(() => parsePlan(planText), [planText]);

  // Approved contacts get a different email — they skip the BD batch deal
  // entirely and download as their own flat list instead.
  const approvedRows = useMemo(() => (rows ?? []).filter(isApproved), [rows]);
  const unapprovedRows = useMemo(() => (rows ?? []).filter((r) => !isApproved(r)), [rows]);

  const split = useMemo(() => {
    if (!rows || unapprovedRows.length === 0 || batches.length === 0) return null;
    const { assignment, audienceStats } = computeSplit(unapprovedRows, batches, seed);
    const counts = batches.map(() => 0);
    const domainCounts = batches.map(() => new Map()); // per batch: domain → n
    for (const [row, b] of assignment) {
      if (b < 0) continue; // matched no pasted audience — not in any batch
      counts[b] += 1;
      const d = domainOf(row);
      domainCounts[b].set(d, (domainCounts[b].get(d) ?? 0) + 1);
    }
    // Top real domains among the SELECTED contacts (what's actually going
    // out), for the spread preview grid.
    const totals = new Map();
    let poolCount = 0;
    for (const [row, b] of assignment) {
      if (b === -2) { poolCount += 1; continue; }
      if (b < 0) continue;
      const d = domainOf(row);
      if (d !== '(no email)' && !FREEMAIL.has(d)) totals.set(d, (totals.get(d) ?? 0) + 1);
    }
    const topDomains = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
    return { assignment, counts, domainCounts, topDomains, audienceStats, poolCount };
  }, [rows, unapprovedRows, batches, seed]);

  const capacity = batches.reduce((a, b) => a + b.size, 0);
  const delta = rows ? unapprovedRows.length - capacity : 0;

  function handleDownload() {
    if (!split) return;
    const hasMeta = batches.some((b) => b.meta);
    const hasAudience = batches.some((b) => b.audience);
    // Only this week's actual sends — unselected pool and no-audience
    // contacts stay out of the upload file (the preview reports their counts).
    const byBatch = [...split.assignment.entries()]
      .filter(([, b]) => b >= 0)
      .sort((a, b) => a[1] - b[1] || String(a[0].name ?? '').localeCompare(String(b[0].name ?? '')));
    downloadCsv(`batches_${new Date().toISOString().slice(0, 10)}.csv`, byBatch.map(([row, b]) => ({
      Batch: batches[b].name,
      ...(hasAudience ? { Audience: batches[b].audience } : {}),
      ...(hasMeta ? { 'Batch Info': batches[b].meta } : {}),
      Name: row.name ?? '',
      Email: row.email ?? '',
      Firm: row._firm_name ?? row.firm_name ?? '',
    })));
  }

  // Approved contacts get their own email, outside the BD batches: a flat
  // list is all that send needs.
  function handleDownloadApproved() {
    if (approvedRows.length === 0) return;
    downloadCsv(`approved_${new Date().toISOString().slice(0, 10)}.csv`,
      [...approvedRows]
        .sort((a, b) => String(a._firm_name ?? a.firm_name ?? '').localeCompare(String(b._firm_name ?? b.firm_name ?? ''))
          || String(a.name ?? '').localeCompare(String(b.name ?? '')))
        .map((row) => ({
          Email: row.email ?? '',
          Fund: row._firm_name ?? row.firm_name ?? '',
          Name: row.name ?? '',
          // Blank packs get the Breakdown grid's label for the same cell —
          // usually "Generalist" (coverage-based), never an empty column.
          'Sector Pack': String(row.sector_packs ?? '').trim() || sectorBlankLabel(row),
        })));
  }

  function namesForBatch(i) {
    return [...split.assignment.entries()]
      .filter(([, b]) => b === i)
      .map(([r]) => String(r.name ?? '').trim())
      .filter(Boolean);
  }

  async function pushToBd(i) {
    const names = namesForBatch(i);
    if (names.length === 0) return;
    try {
      const res = await fetch(`${BD_AGENT}/import`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ref: i, listName: batches[i].saveName ?? batches[i].name, names }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) alert(d.error ?? `BD agent refused (${res.status})`);
      else setAgent(d.job);
    } catch (e) {
      alert(`Could not reach the local BD agent: ${e.message}`);
    }
  }

  // Queue every batch into the agent in one shot. Batches the CURRENT queue
  // already finished are skipped, so re-clicking after a mid-queue stop
  // picks up where it left off instead of making duplicate lists.
  async function pushAllToBd() {
    const doneRefs = new Set(
      (agent?.kind === 'import-queue' ? agent.batches ?? [] : [])
        .filter((b) => b.state === 'done' && b.listName === rowListName(b.ref))
        .map((b) => b.ref)
    );
    const payload = batches
      .map((b, i) => ({ ref: i, listName: b.saveName ?? b.name, names: namesForBatch(i) }))
      .filter((b) => b.names.length > 0 && !doneRefs.has(b.ref));
    if (payload.length === 0) return;
    try {
      const res = await fetch(`${BD_AGENT}/import-all`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ batches: payload }),
      });
      const d = await res.json().catch(() => ({}));
      // A 404 here means the agent process predates this feature — its routes
      // are loaded once at startup.
      if (res.status === 404) alert('The BD agent is running an older version — restart it (Ctrl+C, then `npm run agent` in scripts-bd/) and try again.');
      else if (!res.ok) alert(d.error ?? `BD agent refused (${res.status})`);
      else setAgent(d.job);
    } catch (e) {
      alert(`Could not reach the local BD agent: ${e.message}`);
    }
  }

  // The agent remembers its last job across panel opens and plan edits, so a
  // job result may describe a DIFFERENT plan than the one on screen now. A
  // job (or queue entry) only belongs to row i if the list name matches too —
  // ref alone sticks a stale "✓ 297 added" onto whatever row inherits that
  // position in the next plan.
  function rowListName(i) {
    return batches[i]?.saveName ?? batches[i]?.name;
  }

  function agentCell(i) {
    // A queue run reports per-batch state; rows the queue knows about render
    // that instead of the single-import button.
    const qb = agent?.kind === 'import-queue'
      ? (agent.batches ?? []).find((b) => b.ref === i && b.listName === rowListName(i))
      : null;
    if (qb) {
      switch (qb.state) {
        case 'queued': return '⏳ queued';
        case 'running': return `⌨ ${qb.done}/${qb.total}`;
        case 'waiting-save': return '💾 save in BD…';
        case 'done': return `✓ ${qb.added} added${qb.skipped?.length ? ` · ${qb.skipped.length} skipped` : ''}${qb.savedAs ? ` · saved as "${qb.savedAs}"` : ''}`;
        case 'cancelled': return '✕ cancelled';
        case 'error': return '✗ failed';
        default: return qb.state;
      }
    }
    if ((agent?.kind ?? 'import') !== 'import' || agent?.ref !== i
        || agent.listName !== rowListName(i) || agent.state === 'idle') {
      return (
        <button
          className="btn btn--ghost btn--sm"
          style={{ fontSize: 11.5, padding: '2px 8px' }}
          disabled={AGENT_ACTIVE.has(agent?.state)}
          title={`Fill a BD Advanced list with this batch's names (save it in BD as "${batches[i].saveName ?? batches[i].name}")`}
          onClick={() => pushToBd(i)}
        >→ BD</button>
      );
    }
    switch (agent.state) {
      case 'opening': return '⏳ opening…';
      case 'waiting-login': return '🔑 log into BD…';
      case 'running': return `⌨ ${agent.done}/${agent.total}`;
      case 'done': return `✓ ${agent.added} added${agent.skipped?.length ? ` · ${agent.skipped.length} skipped` : ''}${agent.savedAs ? ` · saved as "${agent.savedAs}"` : ''}`;
      case 'cancelled': return '✕ cancelled';
      case 'error': return `✗ ${agent.message || 'failed'}`;
      default: return agent.state;
    }
  }

  const cellBase = { border: '1px solid var(--c-border)', padding: '4px 10px', fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap' };
  const headBase = { ...cellBase, fontWeight: 700, background: 'var(--c-bg-2)' };
  const leftCell = { ...cellBase, textAlign: 'left' };

  return (
    <div className="panel-overlay" onClick={onClose} style={{ zIndex: 10001 }}>
      <div
        className="panel"
        style={{ width: 'fit-content', minWidth: 560, maxWidth: '95vw', height: 'auto', maxHeight: '90vh', margin: 'auto', borderRadius: 14, display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel__header">
          <div className="panel__title">
            Split into batches{rows ? ` — ${rows.length.toLocaleString()} filtered contact${rows.length === 1 ? '' : 's'}${approvedRows.length > 0 ? ` (${approvedRows.length.toLocaleString()} approved set aside)` : ''}` : ''}
          </div>
          <button className="panel__close" onClick={onClose}>×</button>
        </div>
        <div className="panel__body" style={{ overflow: 'auto', flex: 1 }}>
          {filterSummary.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filtered by</span>
              {filterSummary.map((s) => (
                <span key={s} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, background: 'var(--c-bg-2)', border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}>{s}</span>
              ))}
            </div>
          )}

          <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', marginBottom: 6 }}>
            Paste schedule rows from the planning sheet (Date / Day / Time / Audience / # / Batch name — extra columns are ignored), or just a list of sizes like <code>500, 300, 200</code>.
            With an Audience column, contacts are matched to audiences by sector pack (Generalist = generalist coverage) and each audience splits into its own batches — so you can paste the whole schedule at once. An audience named <b>Everyone else</b> catches every contact that matches none of the other audiences.
          </div>
          <textarea
            value={planText}
            onChange={(e) => setPlanText(e.target.value)}
            placeholder={'9/27/2026\tSunday\t3pm ET\tGeneralist\t500\tGeneralist UA Batch 1\n9/28/2026\tMonday\t2pm ET\tGeneralist\t300\tGeneralist UA Batch 2'}
            rows={5}
            style={{
              width: '100%', boxSizing: 'border-box', fontFamily: 'ui-monospace, monospace', fontSize: 12,
              padding: 8, borderRadius: 8, border: '1px solid var(--c-border)',
              background: 'var(--c-surface)', color: 'var(--c-text)', resize: 'vertical',
            }}
          />

          {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>Failed to load contacts: {error}</div>}
          {!error && !rows && <div style={{ fontSize: 13, color: 'var(--c-text-2)', padding: '14px 0' }}>Loading filtered contacts…</div>}

          {rows && approvedRows.length > 0 && (
            <div style={{ fontSize: 12.5, marginTop: 10, color: 'var(--c-text-2)' }}>
              ✅ {approvedRows.length.toLocaleString()} approved contact{approvedRows.length === 1 ? '' : 's'} set aside — they get a different email, so they're not dealt into the BD batches. Download their list (email / fund / name / sector pack) below.
            </div>
          )}

          {rows && batches.length > 0 && !split?.audienceStats && (
            <div style={{ fontSize: 12.5, marginTop: 10, color: delta >= 0 ? 'var(--c-text-2)' : '#d97706' }}>
              {batches.length} batch{batches.length === 1 ? '' : 'es'}, planned capacity {capacity.toLocaleString()} vs {unapprovedRows.length.toLocaleString()} unapproved contacts
              {delta > 0 && ` — random ${capacity.toLocaleString()} selected, ${delta.toLocaleString()} stay in the pool for a later week`}
              {delta < 0 && ` — batches will come up ${(-delta).toLocaleString()} short, proportionally`}
              {delta === 0 && ' — exact match'}
            </div>
          )}
          {split?.audienceStats && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {[...split.audienceStats.entries()].map(([k, s]) => (
                <span
                  key={k}
                  style={{
                    fontSize: 12, padding: '3px 10px', borderRadius: 999,
                    border: '1px solid var(--c-border)', background: 'var(--c-bg-2)',
                    // A pool bigger than the plan is the normal workflow
                    // (random subset selected); a shortfall is the problem.
                    color: k === '__unmatched' || s.matched < s.capacity ? '#d97706' : 'var(--c-text-2)',
                  }}
                >
                  {k === '__unmatched'
                    ? `⚠ ${s.matched.toLocaleString()} match no audience — not in the CSV`
                    : `${s.label}: ${s.matched.toLocaleString()} matched vs ${s.capacity.toLocaleString()} planned${s.matched > s.capacity ? ` — random ${s.capacity.toLocaleString()} selected` : ''}`}
                </span>
              ))}
              {split.poolCount > 0 && (
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, border: '1px solid var(--c-border)', background: 'var(--c-bg-2)', color: 'var(--c-text-2)' }}>
                  {split.poolCount.toLocaleString()} not selected — stay in the pool for a later week
                </span>
              )}
            </div>
          )}

          {split && (
            <>
              <table style={{ borderCollapse: 'collapse', marginTop: 12 }}>
                <thead>
                  <tr>
                    {/* Same number heads the spread grid's columns below. */}
                    <th style={headBase}>#</th>
                    <th style={{ ...headBase, textAlign: 'left' }}>Batch</th>
                    {batches.some((b) => b.audience) && <th style={{ ...headBase, textAlign: 'left' }}>Audience</th>}
                    {batches.some((b) => b.meta) && <th style={{ ...headBase, textAlign: 'left' }}>When</th>}
                    <th style={headBase}>Planned</th>
                    <th style={headBase}>Assigned</th>
                    <th style={headBase}>Domains</th>
                    <th style={{ ...headBase, textAlign: 'left' }}>Largest single domain</th>
                    {agent && <th style={{ ...headBase, textAlign: 'left' }}>BigDough</th>}
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b, i) => {
                    const dc = split.domainCounts[i];
                    const top = [...dc.entries()].sort((x, y) => y[1] - x[1])[0];
                    return (
                      <tr key={i}>
                        <td style={{ ...cellBase, color: 'var(--c-text-3)' }}>{i + 1}</td>
                        <td style={{ ...leftCell, fontWeight: 600 }}>{b.name}</td>
                        {batches.some((x) => x.audience) && <td style={leftCell}>{b.audience}</td>}
                        {batches.some((x) => x.meta) && <td style={{ ...leftCell, color: 'var(--c-text-2)' }}>{b.meta}</td>}
                        <td style={cellBase}>{b.size.toLocaleString()}</td>
                        <td style={{ ...cellBase, fontWeight: 700, color: split.counts[i] === b.size ? 'var(--c-text)' : '#d97706' }}>
                          {split.counts[i].toLocaleString()}
                        </td>
                        <td style={cellBase}>{dc.size.toLocaleString()}</td>
                        <td style={{ ...leftCell, color: 'var(--c-text-2)' }}>{top ? `${top[0]} (${top[1]})` : '—'}</td>
                        {agent && <td style={leftCell}>{agentCell(i)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {agent === null && (
                <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 8 }}>
                  💡 To fill BigDough lists from here, run <code>npm run agent</code> in <code>scripts-bd/</code> — a "→ BD" button appears on each batch while it's running.
                </div>
              )}
              {agent !== null && batches.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ fontSize: 11.5, padding: '2px 8px', fontWeight: 700 }}
                    disabled={AGENT_ACTIVE.has(agent?.state)}
                    title="Fill AND save every batch's BD list back-to-back — the agent clicks Save between lists and keeps this Mac awake (caffeinate) for the whole run. Batches the queue already finished are skipped on a re-run."
                    onClick={pushAllToBd}
                  >→ BD all ({batches.length} lists)</button>
                  <span style={{ fontSize: 11.5, color: 'var(--c-text-3)' }}>
                    Runs unattended — leave the Mac plugged in; the agent keeps it awake.
                  </span>
                </div>
              )}
              {(agent?.kind ?? 'import') === 'import' && agent?.state === 'done' && agent.skipped?.length > 0 &&
                batches.some((b) => (b.saveName ?? b.name) === agent.listName) && (
                <div style={{ fontSize: 12, color: '#d97706', marginTop: 8 }}>
                  Skipped in "{agent.listName}" (no BD match — add manually):{' '}
                  {agent.skipped.join(', ')}
                </div>
              )}
              {AGENT_ACTIVE.has(agent?.state) && (agent?.kind ?? 'import') === 'import' && (
                <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 8 }}>
                  Importing "{agent.listName}" into BD… when it finishes, <b>save the list in the BD window as "{agent.listName}"</b> before starting the next batch.
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ marginLeft: 8, fontSize: 11.5, padding: '2px 8px' }}
                    onClick={() => fetch(`${BD_AGENT}/cancel`, { method: 'POST' }).catch(() => {})}
                  >Cancel</button>
                </div>
              )}
              {AGENT_ACTIVE.has(agent?.state) && agent?.kind === 'import-queue' && (
                <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 8 }}>
                  {agent.state === 'waiting-save' ? (
                    <>
                      Waiting on a save — <b>click Save in the BD window</b> and the queue continues on its own.
                      If the list already saved but BD kept the form filled,{' '}
                      <button
                        className="btn btn--ghost btn--sm"
                        style={{ fontSize: 11.5, padding: '2px 8px' }}
                        onClick={() => fetch(`${BD_AGENT}/queue-continue`, { method: 'POST' }).catch(() => {})}
                      >mark saved &amp; continue</button>
                    </>
                  ) : (
                    <>
                      Filling {(agent.batches ?? []).length} BD lists back-to-back
                      {agent.current >= 0 ? ` (list ${agent.current + 1}/${(agent.batches ?? []).length})` : ''} —
                      the agent clicks Save between lists and keeps this Mac awake while it runs. Leave the BD window alone.
                    </>
                  )}
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ marginLeft: 8, fontSize: 11.5, padding: '2px 8px' }}
                    onClick={() => fetch(`${BD_AGENT}/cancel`, { method: 'POST' }).catch(() => {})}
                  >Cancel</button>
                </div>
              )}
              {agent?.kind === 'import-queue' && agent.state === 'error' &&
                (agent.batches ?? []).some((b) => b.listName === rowListName(b.ref)) && (
                <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>✗ {agent.message}</div>
              )}
              {agent?.kind === 'import-queue' && ['done', 'error', 'cancelled'].includes(agent.state) &&
                (agent.batches ?? []).some((b) => b.skipped?.length > 0 && b.listName === rowListName(b.ref)) && (
                <div style={{ fontSize: 12, color: '#d97706', marginTop: 8 }}>
                  {(agent.batches ?? []).filter((b) => b.skipped?.length > 0 && b.listName === rowListName(b.ref)).map((b) => (
                    <div key={b.ref}>Skipped in "{b.listName}" (no BD match — add manually): {b.skipped.join(', ')}</div>
                  ))}
                </div>
              )}

              {split.topDomains.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '16px 0 6px' }}>
                    Spread of the biggest domains
                  </div>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...headBase, textAlign: 'left' }}>Domain</th>
                        {batches.map((b, i) => <th key={i} style={headBase} title={b.name}>{i + 1}</th>)}
                        <th style={headBase}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {split.topDomains.map(([d, total]) => (
                        <tr key={d}>
                          <td style={{ ...leftCell, fontWeight: 600 }}>{d}</td>
                          {batches.map((b, i) => {
                            const n = split.domainCounts[i].get(d) ?? 0;
                            return <td key={i} style={{ ...cellBase, color: n === 0 ? 'var(--c-text-3)' : 'var(--c-text)' }}>{n}</td>;
                          })}
                          <td style={{ ...cellBase, fontWeight: 700 }}>{total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 8 }}>
                    Columns are batches in pasted order. Personal-mail domains (gmail etc.) aren't grouped — each contact deals independently.
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--c-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn--ghost btn--sm" disabled={!split} onClick={() => setSeed((s) => s + 1)}>🎲 Reshuffle</button>
          <button
            className="btn btn--ghost btn--sm"
            disabled={approvedRows.length === 0}
            title="Flat list of the approved contacts (email / fund / name / sector pack) — they get a different email than the BD batches"
            onClick={handleDownloadApproved}
          >⬇ Approved CSV{approvedRows.length > 0 ? ` (${approvedRows.length.toLocaleString()})` : ''}</button>
          <button className="btn btn--ghost btn--sm" disabled={!split} onClick={handleDownload} title="One row per unapproved contact with its batch — what the BD lists get">⬇ Batches CSV</button>
          <button className="btn btn--primary btn--sm" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
